import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';

const SupportPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({ name, email, subject, message });
    setSubmitted(true);
    setName('');
    setEmail('');
    setSubject('');
    setMessage('');
  };

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Support | Forecast</title>
        <meta name="description" content="Get help with Forecast. Contact our support team for questions about budgeting, bank sync, subscriptions, or any app issues." />
        <link rel="canonical" href="https://tryforecast.app/support" />
      </Helmet>

      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <span style={styles.backArrow}>&larr;</span> Back
        </button>

        <div style={styles.header}>
          <div style={styles.badge}>Help</div>
          <h1 style={styles.title}>Support Center</h1>
          <p style={styles.subtitle}>We're here to help. Send us a message and we'll get back to you as soon as possible.</p>
        </div>

        <div style={styles.divider} />

        {/* Quick Links */}
        <div style={styles.quickLinks}>
          <a href="mailto:r.dirawatun@gmail.com" style={styles.quickLinkCard}>
            <span style={styles.quickLinkIcon}>&#9993;</span>
            <span style={styles.quickLinkLabel}>Email Us</span>
            <span style={styles.quickLinkValue}>r.dirawatun@gmail.com</span>
          </a>
          <Link to="/privacy-policy" style={styles.quickLinkCard}>
            <span style={styles.quickLinkIcon}>&#128274;</span>
            <span style={styles.quickLinkLabel}>Privacy Policy</span>
            <span style={styles.quickLinkValue}>How we protect your data</span>
          </Link>
          <Link to="/terms-of-service" style={styles.quickLinkCard}>
            <span style={styles.quickLinkIcon}>&#128196;</span>
            <span style={styles.quickLinkLabel}>Terms of Service</span>
            <span style={styles.quickLinkValue}>Usage terms & conditions</span>
          </Link>
        </div>

        {/* Contact Form */}
        <section style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Send a Message</h2>

          {submitted ? (
            <div style={styles.successCard}>
              <div style={styles.successIcon}>&#10003;</div>
              <h3 style={styles.successTitle}>Message Sent!</h3>
              <p style={styles.successText}>We've received your message and will get back to you as soon as possible.</p>
              <button style={styles.sendAnotherButton} onClick={() => setSubmitted(false)}>
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="name">Name</label>
                  <input
                    style={styles.input}
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="email">Email</label>
                  <input
                    style={styles.input}
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="subject">Subject</label>
                <input
                  style={styles.input}
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What's this about?"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="message">Message</label>
                <textarea
                  style={{ ...styles.input, ...styles.textarea }}
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="5"
                  placeholder="Tell us how we can help..."
                  required
                />
              </div>

              <button type="submit" style={styles.submitButton}>
                Send Message
              </button>
            </form>
          )}
        </section>

        <div style={styles.footer}>
          <p style={styles.footerText}>&copy; {new Date().getFullYear()} Forecast &middot; San Diego, CA</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)',
    padding: '24px 16px 60px',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '40px 36px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    color: '#6366F1',
    padding: '8px 0',
    marginBottom: 24,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  backArrow: {
    fontSize: 18,
  },
  header: {
    textAlign: 'center',
    marginBottom: 8,
  },
  badge: {
    display: 'inline-block',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#6366F1',
    background: 'rgba(99,102,241,0.08)',
    padding: '6px 16px',
    borderRadius: 20,
    marginBottom: 16,
  },
  title: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 800,
    fontSize: 36,
    color: '#0F172A',
    margin: '0 0 12px',
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  subtitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 16,
    color: '#94A3B8',
    margin: 0,
    lineHeight: 1.5,
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg, transparent, #E2E8F0, transparent)',
    margin: '28px 0 32px',
  },
  quickLinks: {
    display: 'flex',
    gap: 12,
    marginBottom: 36,
    flexWrap: 'wrap',
  },
  quickLinkCard: {
    flex: '1 1 200px',
    background: '#F8FAFC',
    borderRadius: 16,
    padding: '20px',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    border: '1px solid #F1F5F9',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  quickLinkIcon: {
    fontSize: 22,
    marginBottom: 4,
  },
  quickLinkLabel: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 15,
    color: '#1E293B',
  },
  quickLinkValue: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    color: '#94A3B8',
  },
  formSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 700,
    fontSize: 20,
    color: '#1E293B',
    margin: '0 0 20px',
    letterSpacing: '-0.01em',
  },
  formRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  formGroup: {
    flex: '1 1 280px',
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    fontSize: 14,
    color: '#475569',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15,
    color: '#1E293B',
    background: '#F8FAFC',
    border: '1.5px solid #E2E8F0',
    borderRadius: 12,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  textarea: {
    resize: 'vertical',
    minHeight: 120,
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 16,
    color: '#FFFFFF',
    background: '#6366F1',
    border: 'none',
    borderRadius: 14,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'background 0.2s',
  },
  successCard: {
    textAlign: 'center',
    background: '#F0FDF4',
    borderRadius: 16,
    padding: '40px 24px',
    border: '1px solid #BBF7D0',
  },
  successIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#22C55E',
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  successTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 700,
    fontSize: 22,
    color: '#166534',
    margin: '0 0 8px',
  },
  successText: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15,
    color: '#4ADE80',
    margin: '0 0 20px',
    lineHeight: 1.5,
  },
  sendAnotherButton: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    fontSize: 14,
    color: '#6366F1',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  footer: {
    textAlign: 'center',
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid #F1F5F9',
  },
  footerText: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    color: '#CBD5E1',
    margin: 0,
  },
};

export default SupportPage;
